import { MOTION } from '@/utils';
import { withBasePath } from '@/utils/basePath';
import { TESTIMONIALS } from '@/utils/data/testimonials';
import { useGSAP } from '@gsap/react';
import { gsap } from 'gsap';
import { SplitText } from 'gsap/SplitText';
import Image from 'next/image';
import { forwardRef, useRef } from 'react';
type TestimonialCardProps = {
  cabinet: string;
  client: string;
  description: string;
  logo: string;
};

const TestimonialCard = forwardRef<HTMLDivElement, TestimonialCardProps>(
  ({ cabinet, client, description, logo }, ref) => {
    return (
      <article
        ref={ref}
        className="relative w-[90%] shrink-0 rounded-(--radius-current) bg-white p-[30px] shadow-(--shadow-current) md:w-[70%] xl:w-[38vw] xl:max-w-[700px] xl:p-[50px]"
      >
        <Image
          alt="guillemet de citation"
          className="absolute -top-5 right-[10%] h-[60px] w-[60px]"
          height={60}
          loading="lazy"
          src={withBasePath('/images/logos/quoteLogo.svg')}
          width={60}
        />

        <Image
          alt="logo du client"
          className="mb-10 ml-5 h-[50px] w-auto"
          height={80}
          loading="lazy"
          src={logo}
          width={160}
        />

        <div className="border-blue border-l-2 pl-2">
          <p>{description}</p>
        </div>

        <p className="mt-[50px] self-end text-right">
          <span className="font-bold">{client}</span>
          <br />
          {cabinet}
        </p>
      </article>
    );
  },
);

TestimonialCard.displayName = 'TestimonialCard';

function SliderArrow({
  alt,
  direction,
  onClick,
}: {
  alt: string;
  direction: 'left' | 'right';
  onClick: () => void;
}) {
  return (
    <button
      aria-label={alt}
      className="inline-flex h-12 w-12 items-center justify-center"
      type="button"
      onClick={onClick}
    >
      <Image
        alt={alt}
        className="h-5 w-auto cursor-pointer"
        height={20}
        loading="lazy"
        src={withBasePath(`/images/logos/arrow${direction === 'left' ? 'Left' : 'Right'}Icon.svg`)}
        width={20}
      />
    </button>
  );
}

export default function Testimonials() {
  const sectionRef = useRef<HTMLElement>(null);
  const titleRef = useRef<HTMLElement | null>(null);
  const sliderContainerRef = useRef<HTMLDivElement>(null);
  const sliderCardRef = useRef<HTMLDivElement>(null);
  const cardsRef = useRef<HTMLDivElement[]>([]);
  const arrowsRef = useRef<HTMLDivElement>(null);

  useGSAP(() => {
    if (!sectionRef.current || !titleRef.current) return;

    const splitTitle = new SplitText(titleRef.current, {
      mask: 'lines',
      type: 'lines',
    });

    const cards = cardsRef.current.filter(Boolean);
    const arrows = arrowsRef.current ? Array.from(arrowsRef.current.children) : [];

    const tl = gsap.timeline({
      defaults: {
        duration: MOTION.durationMd,
        ease: MOTION.ease,
      },
      scrollTrigger: {
        start: 'top 50%',
        toggleActions: 'play none none reverse',
        trigger: sectionRef.current,
      },
    });

    tl.from(splitTitle.lines, {
      stagger: MOTION.staggerLines,
      yPercent: MOTION.yPercent,
    })
      .from(
        cards,
        {
          autoAlpha: 0,
          stagger: 0.1,
          x: MOTION.y,
          y: MOTION.y,
        },
        '<+0.2',
      )
      .from(
        arrows,
        {
          autoAlpha: 0,
          duration: MOTION.durationSm,
          scale: 0.9,
          stagger: 0.1,
          y: MOTION.y,
        },
        '<+0.15',
      );

    return () => {
      splitTitle.revert();
    };
  }, []);

  const getScrollStep = () => {
    if (!sliderContainerRef.current || !sliderCardRef.current) return 0;

    const styles = window.getComputedStyle(sliderContainerRef.current);
    const gap = Number.parseFloat(styles.columnGap || styles.gap || '0');

    return sliderCardRef.current.clientWidth + gap;
  };

  const scrollRight = () => {
    if (!sliderContainerRef.current) return;

    const container = sliderContainerRef.current;
    const step = getScrollStep();

    if (container.scrollLeft + container.clientWidth >= container.scrollWidth - 1) {
      container.scrollTo({
        behavior: 'smooth',
        left: 0,
      });
      return;
    }

    container.scrollTo({
      behavior: 'smooth',
      left: container.scrollLeft + step,
    });
  };

  const scrollLeft = () => {
    if (!sliderContainerRef.current) return;

    const container = sliderContainerRef.current;
    const step = getScrollStep();

    if (container.scrollLeft <= 1) {
      container.scrollTo({
        behavior: 'smooth',
        left: container.scrollWidth - container.clientWidth,
      });
      return;
    }

    container.scrollTo({
      behavior: 'smooth',
      left: container.scrollLeft - step,
    });
  };

  return (
    <section ref={sectionRef} className="relative py-32" id="clients">
      <h2
        ref={(element) => {
          titleRef.current = element;
        }}
        className="mb-20 px-[clamp(20px,5vw,130px)]"
      >
        Témoignages
      </h2>

      <div
        ref={sliderContainerRef}
        className="flex gap-10 overflow-x-auto px-[clamp(20px,5vw,130px)] py-5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {TESTIMONIALS.map((item, index) => (
          <TestimonialCard
            key={`${item.client}-${item.cabinet}`}
            ref={(element) => {
              if (element) cardsRef.current[index] = element;
              if (index === 0) {
                sliderCardRef.current = element;
              }
            }}
            cabinet={item.cabinet}
            client={item.client}
            description={item.description}
            logo={item.logo}
          />
        ))}
      </div>

      <div ref={arrowsRef} className="mt-10 flex justify-center gap-20">
        <SliderArrow alt="Aller au témoignage précédent" direction="left" onClick={scrollLeft} />
        <SliderArrow alt="Aller au témoignage suivant" direction="right" onClick={scrollRight} />
      </div>
    </section>
  );
}
